// The form to submit a Skill or a Plugin: the same three fields for a visitor and for the administrator, the same words for what
// goes wrong, and for the visitor an optional human check when the market has one switched on.

import { ApiFailure, api, get } from './api.ts';
import type { Kind, PackageDiscovery } from './types.ts';
import { openPackageSelection } from './package-selection.ts';
import { KIND_NAME, esc, must, onAct, openModal } from './util.ts';

export type SubmitMode = 'user' | 'admin';

interface Turnstile {
  render(element: HTMLElement, options: { sitekey: string; callback: (token: string) => void; 'expired-callback': () => void }): string;
  reset(id: string): void;
}
declare global {
  interface Window {
    turnstile?: Turnstile;
  }
}

let turnstileLoaded: Promise<void> | null = null;
function loadTurnstile(): Promise<void> {
  turnstileLoaded ??= new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => { turnstileLoaded = null; script.remove(); reject(new Error('人机验证无法加载')); };
    document.head.appendChild(script);
  });
  return turnstileLoaded;
}

const field = (form: HTMLFormElement, name: string): HTMLInputElement => form.elements.namedItem(name) as HTMLInputElement;

function failureHtml(failure: ApiFailure): string {
  const problems = failure.body.problems?.length ? `<ul style="margin:8px 0 0;padding-left:18px">${failure.body.problems.map((problem) => `<li>${esc(problem.message)}</li>`).join('')}</ul>` : '';
  const suggestions = failure.body.suggestions?.length
    ? `<div style="margin-top:10px">可以改填目录：<div style="display:flex;gap:6px;flex-wrap:wrap;margin-top:6px">${failure.body.suggestions
        .map((path) => `<button type="button" class="btn btn-sm" data-act="use-path" data-path="${esc(path)}">${esc(path)}</button>`)
        .join('')}</div></div>`
    : '';
  return `<div class="note err">${esc(failure.message)}${problems}${suggestions}</div>`;
}

/** Looks through a repository for skills and for bundles, without asking the person to pick a page first. */
export function openContentsSubmit(mode: SubmitMode, preset: { url?: string; ref?: string; path?: string } = {}): void {
  const admin = mode === 'admin';
  const verb = admin ? '添加' : '提交';
  openModal(`<div class="modal-head"><h2>${verb}内容</h2><button class="x" data-act="close" aria-label="关闭">×</button></div>
  <p class="modal-sub">填写公开的 GitHub 仓库。可以是一个做法，也可以是带有多个做法、服务或页面的一整包。经过安全检测和管理员审核后才会公开。</p>
  <form id="submit-form" autocomplete="off" novalidate>
    <label class="field"><span>GitHub 仓库链接</span><input name="url" placeholder="https://github.com/owner/repo" value="${esc(preset.url ?? '')}"></label>
    <label class="field"><span>分支或版本<em>选填</em></span><input name="ref" placeholder="留空使用仓库默认分支" value="${esc(preset.ref ?? '')}"></label>
    <label class="field"><span>仓库内目录<em>选填</em></span><input name="path" placeholder="例如 plugins/hyperframes" value="${esc(preset.path ?? '')}">
      <div class="hint">留空查找整个仓库。一整包会连同里面的做法一起提交，不会拆开。</div></label>
    <div id="human-check"></div>
    <div id="submit-message"></div>
    <div class="modal-actions"><button type="button" class="btn" data-act="close">取消</button><button class="btn-primary" id="submit-go">查找内容</button></div>
  </form>`);
  const form = must<HTMLFormElement>('#submit-form');
  const button = must<HTMLButtonElement>('#submit-go');
  const message = must('#submit-message');
  let reading = false;
  const update = () => { button.disabled = reading || field(form, 'url').value.trim() === ''; };
  form.addEventListener('input', () => { update(); message.innerHTML = ''; });
  update();
  setTimeout(() => field(form, 'url').focus(), 30);
  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (button.disabled) return;
    reading = true;
    button.disabled = true;
    button.innerHTML = '<span class="spin"></span> 正在读取仓库…';
    message.innerHTML = '';
    const body = { url: field(form, 'url').value, ref: field(form, 'ref').value, path: field(form, 'path').value };
    try {
      const discovery = await api<PackageDiscovery>('POST', admin ? '/api/admin/packages/discover' : '/api/v1/packages/discover', body);
      if (!form.isConnected) return;
      openPackageSelection(discovery, mode, body);
    } catch (thrown) {
      if (!form.isConnected) return;
      reading = false;
      button.textContent = '查找内容';
      update();
      message.innerHTML = thrown instanceof ApiFailure ? failureHtml(thrown) : '<div class="note err">出了点问题，请稍后再试。</div>';
    }
  });
}

export function openSubmit(kind: Kind, mode: SubmitMode, preset: { url?: string; ref?: string; path?: string } = {}): void {
  const name = KIND_NAME[kind];
  const admin = mode === 'admin';
  const verb = admin ? '添加' : '提交';
  const action = `查找 ${name}`;
  const plural = kind === 'skill' ? 'skills' : 'plugins';
  openModal(`<div class="modal-head"><h2>${verb} ${name}</h2><button class="x" data-act="close" aria-label="关闭">×</button></div>
  <p class="modal-sub">填写公开的 GitHub 仓库，自动查找其中的 ${name}，再选择要${verb}的内容。经过安全检测和管理员审核后才会公开。</p>
  <form id="submit-form" autocomplete="off" novalidate>
    <label class="field"><span>GitHub 仓库链接</span><input name="url" placeholder="https://github.com/owner/repo" value="${esc(preset.url)}"></label>
    <label class="field"><span>分支或版本<em>选填</em></span><input name="ref" placeholder="留空使用仓库默认分支" value="${esc(preset.ref)}"></label>
    <label class="field"><span>仓库内目录<em>选填</em></span><input name="path" placeholder="${kind === 'skill' ? '例如 skills/pdf-extract' : '例如 plugins/research-kit'}" value="${esc(preset.path)}">
      <div class="hint">留空查找整个仓库；填写目录只查找该目录内的 ${name}。${kind === 'plugin' ? '每个 Plugin 整体提交，包含的 Skill 会一同收录。' : ''}</div></label>
    <div id="human-check"></div>
    <div id="submit-message"></div>
    <div class="modal-actions"><button type="button" class="btn" data-act="close">取消</button><button class="btn-primary" id="submit-go">${action}</button></div>
  </form>`);
  const form = must<HTMLFormElement>('#submit-form');
  const button = must<HTMLButtonElement>('#submit-go');
  const message = must('#submit-message');
  let reading = false;

  const update = () => {
    button.disabled = reading || field(form, 'url').value.trim() === '';
  };
  form.addEventListener('input', () => {
    update();
    message.innerHTML = '';
  });
  update();
  setTimeout(() => field(form, 'url').focus(), 30);

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (button.disabled) return;
    reading = true;
    button.disabled = true;
    button.innerHTML = '<span class="spin"></span> 正在读取仓库…';
    message.innerHTML = '';
    const body = { type: kind, url: field(form, 'url').value, ref: field(form, 'ref').value, path: field(form, 'path').value };
    try {
      const discovery = await api<PackageDiscovery>('POST', admin ? `/api/admin/${plural}/discover` : `/api/v1/${plural}/discover`, body);
      if (!form.isConnected) return;
      openPackageSelection(discovery, mode, { url: body.url, ref: body.ref, path: body.path });
    } catch (thrown) {
      if (!form.isConnected) return;
      reading = false;
      button.textContent = action;
      update();
      message.innerHTML = thrown instanceof ApiFailure ? failureHtml(thrown) : '<div class="note err">出了点问题，请稍后再试。</div>';
    }
  });
}

/** A fresh human check per actual batch, including retries; discovery itself creates no submissions. */
export async function selectionHumanCheck(holder: HTMLElement, onToken: (token: string) => void): Promise<{ required: boolean; reset(): void }> {
  const { turnstileSiteKey } = await get<{ turnstileSiteKey: string | null }>('/api/v1/config');
  if (!holder.isConnected || !turnstileSiteKey) return { required: false, reset: () => undefined };
  await loadTurnstile();
  if (!holder.isConnected) return { required: true, reset: () => undefined };
  if (!window.turnstile) throw new Error('人机验证无法加载');
  const label = document.createElement('div');
  label.className = 'field';
  label.textContent = '人机验证';
  const widgetHolder = document.createElement('div');
  holder.replaceChildren(label, widgetHolder);
  const id = window.turnstile.render(widgetHolder, { sitekey: turnstileSiteKey, callback: onToken, 'expired-callback': () => onToken('') });
  return { required: true, reset: () => { onToken(''); window.turnstile?.reset(id); } };
}

onAct('use-path', (element) => {
  const input = document.querySelector<HTMLInputElement>('#submit-form input[name=path]');
  if (!input) return;
  input.value = element.dataset.path ?? '';
  input.dispatchEvent(new Event('input', { bubbles: true }));
  input.focus();
});
